import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scientificPartnerApi } from '@/features/workspace/api';

interface RequestItem {
  reference_code: string;
  service_name: string;
  client_name: string;
  client_code: string;
  status: string;
  priority: string;
  current_stage: string;
  due_date: string;
  created_at: string;
}

export const PartnerRequestsView: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<RequestItem[]>([]);
  const [filter, setFilter] = useState<string>('ALL');
  const [selectedReq, setSelectedReq] = useState<RequestItem | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newTopic, setNewTopic] = useState('');

  const loadRequests = async () => {
    try {
      const res = await scientificPartnerApi.module('requests', { filter });
      if (res && Array.isArray(res.items)) {
        setItems(res.items as RequestItem[]);
      }
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName.trim()) return;
    try {
      await scientificPartnerApi.createRequest({
        service_name: newServiceName,
        topic: newTopic || 'Новая научная заявка',
      });
      setIsCreateOpen(false);
      setNewServiceName('');
      setNewTopic('');
      loadRequests();
    } catch {
      setIsCreateOpen(false);
    }
  };

  const getStatusBadge = (st: string) => {
    switch (st) {
      case 'IN_PROGRESS':
        return { label: 'В работе', bg: '#0284c7', color: '#ffffff' };
      case 'NEEDS_CLARIFICATION':
        return { label: 'Требует уточнения', bg: '#f59e0b', color: '#0f172a' };
      case 'NOT_STARTED':
        return { label: 'Не запущена', bg: '#64748b', color: '#ffffff' };
      case 'COMPLETED':
        return { label: 'Завершена', bg: '#10b981', color: '#ffffff' };
      default:
        return { label: st, bg: '#334155', color: '#cbd5e1' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0f172a',
          padding: '0.75rem 1.25rem',
          borderRadius: '10px',
          border: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            onClick={() => navigate('/app/partner')}
            style={{
              padding: '0.4rem 0.85rem',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#f8fafc',
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            ← Назад
          </button>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
              Мои заявки
            </h2>
            <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
              Altyn Bakytzhanova · ZA000003
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            style={{
              padding: '0.45rem 1rem',
              backgroundColor: '#fbbf24',
              border: 'none',
              borderRadius: '6px',
              color: '#0f172a',
              fontWeight: 700,
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            ＋ Новая заявка
          </button>
          <button
            type="button"
            onClick={() => navigate('/app/partner')}
            style={{
              padding: '0.4rem 0.85rem',
              backgroundColor: 'transparent',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#94a3b8',
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            Закрыть ✕
          </button>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #1e293b',
          borderRadius: '12px',
          backgroundColor: '#0f172a',
          padding: '1.25rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <span style={{ fontSize: '0.75rem', color: '#38bdf8', textTransform: 'uppercase', fontWeight: 600 }}>
              Реестр заявок
            </span>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f8fafc', margin: '2px 0 0 0' }}>
              {items.length} заявок
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '0.8125rem', margin: '4px 0 0 0' }}>
              Демонстрационные и рабочие заявки научного партнёра.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['ALL', 'IN_PROGRESS', 'NEEDS_CLARIFICATION', 'COMPLETED'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                style={{
                  padding: '0.4rem 0.85rem',
                  backgroundColor: filter === f ? '#0284c7' : '#1e293b',
                  color: filter === f ? '#ffffff' : '#cbd5e1',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {f === 'ALL'
                  ? 'Все'
                  : f === 'IN_PROGRESS'
                  ? 'В работе'
                  : f === 'NEEDS_CLARIFICATION'
                  ? 'Уточнение'
                  : 'Завершённые'}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>0</div>
            <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>
              Заявок пока нет
            </strong>
            <span>Созданная локальная заявка появится здесь после подтверждения.</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1rem' }}>
            {items.map((item) => {
              const badge = getStatusBadge(item.status);
              return (
                <div
                  key={item.reference_code}
                  style={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    padding: '1.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '1rem',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#38bdf8' }}>
                        {item.reference_code}
                      </span>
                      <span
                        style={{
                          backgroundColor: badge.bg,
                          color: badge.color,
                          padding: '0.15rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>

                    <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#f8fafc', margin: '0 0 0.5rem 0' }}>
                      {item.service_name}
                    </h4>
                    <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                      {item.client_name} · {item.client_code}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#cbd5e1', backgroundColor: '#0f172a', padding: '0.5rem 0.75rem', borderRadius: '6px' }}>
                      <strong>Шаг:</strong> {item.current_stage}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #334155' }}>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      Срок: {item.due_date}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedReq(item)}
                      style={{
                        padding: '0.35rem 0.85rem',
                        backgroundColor: '#0284c7',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Открыть
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedReq && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '1.75rem',
              maxWidth: '560px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 700 }}>
                  {selectedReq.reference_code}
                </span>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: '2px 0 0 0' }}>
                  {selectedReq.service_name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedReq(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
              <div><strong>Заявитель:</strong> {selectedReq.client_name} ({selectedReq.client_code})</div>
              <div><strong>Текущий статус:</strong> {selectedReq.status}</div>
              <div><strong>Следующий шаг:</strong> {selectedReq.current_stage}</div>
              <div><strong>Срок исполнения:</strong> {selectedReq.due_date}</div>
              <div><strong>Приоритет:</strong> {selectedReq.priority}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedReq(null);
                  navigate('/app/partner/academic_connections');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #38bdf8',
                  borderRadius: '6px',
                  color: '#38bdf8',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Обсудить в связях
              </button>
              <button
                type="button"
                onClick={() => setSelectedReq(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0284c7',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreateOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <form
            onSubmit={handleCreate}
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '1.75rem',
              maxWidth: '520px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
                Создать новую заявку
              </h3>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.25rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>
                Название услуги
              </label>
              <input
                type="text"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="Например: Функциональный аудит статьи"
                required
                style={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '0.65rem 0.85rem',
                  color: '#f8fafc',
                  fontSize: '0.875rem',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 600 }}>
                Тема исследования
              </label>
              <input
                type="text"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                placeholder="Например: Корпусная лингвистика и NLP"
                style={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '0.65rem 0.85rem',
                  color: '#f8fafc',
                  fontSize: '0.875rem',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#cbd5e1',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                type="submit"
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#fbbf24',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#0f172a',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Подтвердить
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
